import { useQuery } from '@tanstack/react-query'
import { CalendarOff, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { errorMessage } from '@/api/errors'
import { EmptyState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { PageHeader } from '@/components/ui/PageHeader'
import { holidaysQuery, useDeleteHoliday } from '@/features/attendance/api'
import { HolidayDialog } from '@/features/attendance/components/HolidayDialog'
import { formatLongDay, todayIso } from '@/features/attendance/dates'
import { holidayTypeLabel, holidayTypeTone } from '@/features/attendance/status'
import type { Holiday } from '@/features/attendance/types'
import { Can } from '@/features/auth/guards'
import { useAuth } from '@/features/auth/useAuth'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

export function HolidaysPage() {
  const { t } = useTranslation('attendance')
  useDocumentTitle('Holidays')
  const holidays = useQuery(holidaysQuery())
  const remove = useDeleteHoliday()
  const { can } = useAuth()
  const canManage = can('academic.manage')
  const [editing, setEditing] = useState<Holiday | 'new' | null>(null)
  const [deleting, setDeleting] = useState<Holiday | null>(null)
  const today = todayIso()

  const actions = (h: Holiday) => (
    <div className="flex justify-end gap-1">
      <IconButton
        label={t('Edit {{name}}', { name: h.name })}
        icon={<Pencil />}
        size="icon-sm"
        onClick={() => setEditing(h)}
      />
      <IconButton
        label={t('Delete {{name}}', { name: h.name })}
        icon={<Trash2 />}
        size="icon-sm"
        className="hover:text-danger-600"
        onClick={() => setDeleting(h)}
      />
    </div>
  )

  const columns: Column<Holiday>[] = [
    {
      key: 'date',
      header: t('Date'),
      cell: (h) => (
        <span className={h.date < today ? 'text-muted' : 'font-medium text-ink'}>
          {formatLongDay(h.date)}
          {h.date === today ? (
            <Badge tone="brand" size="sm" className="ml-2">
              {t('Today')}
            </Badge>
          ) : null}
        </span>
      ),
    },
    { key: 'name', header: t('Name'), cell: (h) => <span className="text-ink">{h.name}</span> },
    {
      key: 'type',
      header: t('Type'),
      hideBelow: 'md',
      cell: (h) => <Badge tone={holidayTypeTone[h.type]}>{t(holidayTypeLabel[h.type])}</Badge>,
    },
    ...(canManage
      ? [
          {
            key: 'actions',
            header: <span className="sr-only">{t('Actions')}</span>,
            align: 'right' as const,
            cell: actions,
          },
        ]
      : []),
  ]

  const confirmDelete = async () => {
    if (!deleting) return
    try {
      await remove.mutateAsync(deleting.id)
      toast.success(t('Holiday deleted'), deleting.name)
    } catch (e) {
      toast.error(t('Could not delete'), errorMessage(e))
    } finally {
      setDeleting(null)
    }
  }

  return (
    <>
      <PageHeader
        title={t('Holidays')}
        description={t(
          "Days the school is closed on an otherwise open weekday. Weekly closures are set in the school's opening hours.",
        )}
        breadcrumbs={[{ label: t('Attendance'), to: '/admin/attendance' }, { label: t('Holidays') }]}
        homeHref="/admin"
        actions={
          <Can permission="academic.manage">
            <Button leftIcon={<Plus />} onClick={() => setEditing('new')}>
              {t('Add holiday')}
            </Button>
          </Can>
        }
      />
      <Card className="overflow-hidden">
        <DataTable
          caption={t('Holidays')}
          columns={columns}
          rows={holidays.data}
          getRowId={(h) => h.id}
          loading={holidays.isPending}
          error={holidays.error}
          onRetry={() => void holidays.refetch()}
          skeletonRows={4}
          mobileCard={(h) => (
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-ink">{h.name}</p>
                <p className="text-xs text-muted">
                  {formatLongDay(h.date)} · {t(holidayTypeLabel[h.type])}
                </p>
              </div>
              {canManage ? actions(h) : null}
            </div>
          )}
          empty={
            <EmptyState
              icon={<CalendarOff />}
              title={t('No holidays yet')}
              description={t('Add public holidays and closures so they are skipped when taking attendance.')}
              compact
            />
          }
        />
      </Card>

      {editing !== null ? (
        <HolidayDialog holiday={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      ) : null}
      {deleting ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => (open ? undefined : setDeleting(null))}
          title={t('Delete {{name}}?', { name: deleting.name })}
          description={t('The day becomes a normal working day again.')}
          confirmLabel={t('Delete')}
          loading={remove.isPending}
          onConfirm={() => void confirmDelete()}
        />
      ) : null}
    </>
  )
}
